import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AlertFeedback, AlertVerdictSubmission } from 'app/shared/models/alert-feedback.model';

/**
 * The verdict a reviewer records on an anomaly: Real, Not an issue, or Ask DEO.
 *
 * Dismissing opens an optional reason field. It is optional deliberately — the reason is what the
 * agent learns from, but a reviewer clearing noise is not made to justify it, so saving with an
 * empty field still dismisses.
 *
 * Once a verdict is in, the buttons collapse to the recorded line: the reviewer sees what they
 * decided instead of a row that vanished.
 */
@Component({
  selector: 'gains-feedback-row',
  standalone: false,
  templateUrl: './gains-feedback-row.component.html',
  styleUrl: './gains-feedback-row.component.scss'
})
export class GainsFeedbackRowComponent {
  /** The verdict already recorded, if any. Present means the actions are done. */
  @Input() feedback?: AlertFeedback;

  /**
   * Whether to offer Ask DEO at all.
   *
   * Defaults to on so a host that knows nothing about agents keeps the behaviour it had; the Alerts
   * tray turns it off for an anomaly whose owning agent is not released, since there is nobody to
   * ask. The verdict buttons are never affected — a reviewer must be able to judge an anomaly
   * whether or not they can talk to the agent that raised it.
   */
  @Input() canAskDeo = true;

  @Output() verdictGiven$ = new EventEmitter<AlertVerdictSubmission>();

  @Output() askDeo$ = new EventEmitter<void>();

  /** True while the reason field is open, between Not an issue and saving. */
  isCapturingReason = false;

  reason = '';

  readonly reasonPlaceholder = 'Why is this not an issue? The agent learns from this.';

  get isRecorded(): boolean {
    return !!this.feedback;
  }

  /** What the reviewer decided, in the words the agent will report back. */
  get recordedLabel(): string {
    return this.feedback?.verdict === 'real' ? 'Confirmed — assigned' : 'Dismissed — agent noted it';
  }

  get isConfirmed(): boolean {
    return this.feedback?.verdict === 'real';
  }

  confirmReal(): void {
    this.verdictGiven$.emit({ verdict: 'real' });
  }

  startDismissal(): void {
    this.isCapturingReason = true;
  }

  /** Saves the dismissal with whatever reason was given, including none. */
  dismiss(): void {
    this.verdictGiven$.emit({ verdict: 'not-an-issue', reason: this.reason });
    this.isCapturingReason = false;
  }

  onReasonChange(reason: string): void {
    this.reason = reason ?? '';
  }

  askDeo(): void {
    this.askDeo$.emit();
  }
}
